import { round } from "lodash";
import { ObjectUtils } from "~app/common/utils/object-utils";

export type InputProcessor = {
    formatOutput: (value: any) => string;
    processInput: (value: string) => any;
    useBoundValue: boolean;
};

export const decimalInputPattern = /^-?(\d+(\.\d*)?)?$/;

export class InputProcessorType {
    /**
     * Används för heltal som visas med tusentalsavgränsare
     */
    static readonly seLocaleIntProcessor: InputProcessor = {
        formatOutput: (value) => {
            if (typeof (value) !== "number") {
                return "";
            }

            return value.toLocaleString("sv-SE");
        },
        processInput: (value) => {
            const sanitized = value.replace(/,/g, ".")
                .replace(/\s/g, "");
            const int = parseInt(sanitized, 10);
            return isNaN(int) ? null : int;
        },
        useBoundValue: true
    };

    /**
     * Används för heltal
     */
    static readonly numberIntProcessor: InputProcessor = {
        formatOutput: (value) => {
            if (ObjectUtils.isNullOrUndefined(value) || isNaN(value)) {
                return value;
            }

            return value.toLocaleString("sv-SE");
        },
        // eslint-disable-next-line sonarjs/function-return-type
        processInput: (value) => {
            const sanitized = value
                .replace(/,/g, ".")
                .replace(/\s/g, "")
                .replace(/−/g, "-") // toLocaleString("sv-SE") byter ut minus mot U+2212 vilket leder till problem vid parseInt
                .replace(/[a-zA-Z]/g, "");
            if (sanitized === "-") {
                return sanitized;
            }
            const int = parseInt(sanitized, 10);
            return isNaN(int) ? null : int;
        },
        useBoundValue: true
    };

    /**
    * Används för decimaltal
*/
    static readonly numberDecimalProcessor = (maximumFractionDigits = 20, useBoundValue = false): InputProcessor => ({
        formatOutput: (value) => {
            if (ObjectUtils.isNullOrUndefined(value) || isNaN(value)) {
                return value;
            }

            return value.toLocaleString("sv-SE", {
                maximumFractionDigits: maximumFractionDigits
            });
        },
        // eslint-disable-next-line sonarjs/function-return-type
        processInput: (value) => {
            const sanitized = value
                .replace(/,/g, ".")
                .replace(/\s/g, "")
                .replace(/−/g, "-"); // toLocaleString("sv-SE") byter ut minus mot U+2212 vilket leder till problem vid parseFloat

            // Ser til att värdet förhåller sig till rätt format
            if (!decimalInputPattern.test(sanitized)) {
                return null;
            }

            // Kollar så att första tecknet inte är en punkt samt så att värdet inte slutar på
            if (sanitized.endsWith(".")) {
                return sanitized;
            }

            const val = parseFloat(sanitized);
            return isNaN(val) ? null : round(val, maximumFractionDigits);
        },
        useBoundValue: useBoundValue
    });
}
